import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { Config } from '../config';

@Injectable({
  providedIn: 'root',
})
export class DriverImportService {
  private readonly baseUrl = `${Config.apiRoot.uri}driver_import`;

  constructor(private http: HttpClient, private router: Router) {}

  getSavedMapping(companyName) {
    return this.http.get(`${this.baseUrl}/${companyName}/configs?limit=1`);
  }

  getAllColumnDefs(companyName: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/configs/columns`);
  }

  getAllConfigurations(companyName: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/configs`, {
      reportProgress: true,
      observe: 'events',
    });
  }

  getLatestConfiguration(companyName: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/configs?limit=1`);
  }

  createConfiguration(companyName: string, configData: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${companyName}/configs`, configData);
  }

  updateConfiguration(companyName: string, configId: string, configData: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/${companyName}/configs/${configId}`, configData);
  }

  deleteConfiguration(companyName: string, configId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${companyName}/configs/${configId}`);
  }

  approveProcessing(companyName: string, jobId: string, userId: number, reject: boolean): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/${companyName}/jobs/${jobId}/approve`,
      {},
      {
        params: { approvedBy: userId, reject: reject.toString() }, // Query parameters
        reportProgress: true,
        observe: 'events',
      }
    );
  }
  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remove data:mime/type;base64,
        resolve(base64String);
      };

      reader.onerror = (error) => reject(error);
    });
  }

  createImportJob(
    companyName: string,
    createdBy: number,
    type: string,
    configId: string,
    bypassApproval: boolean,
    emails: string[],
    webhookUrl: string = '',
    file: File
  ): Observable<any> {
    const url = `${this.baseUrl}/${companyName}/jobs/import`;
    const params = new URLSearchParams({
      type,
      configId,
      bypassApproval: bypassApproval.toString(),
      emails: emails.join(','),
      webhookUrl: webhookUrl,
      createdBy: createdBy.toString(),
      returnUntilValidation: 'false',
    });

    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post(`${url}?${params.toString()}`, formData, {
      reportProgress: true,
      observe: 'events',
      headers: new HttpHeaders({
        Accept: '*/*',
      }),
    });
  }

  getJobHistory(companyName: string, startDate?: string, endDate?: string): Observable<HttpEvent<any>> {
    let params = new HttpParams().set('limit', '1000');

    if (startDate) {
      params = params.set('startDate', startDate);
    }
    if (endDate) {
      params = params.set('endDate', endDate);
    }

    return this.http.get(`${this.baseUrl}/${companyName}/jobs`, {
      params,
      reportProgress: true,
      observe: 'events',
    });
  }

  getJobDetails(companyName: string, jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/jobs/${jobId}`);
  }

  downloadJobFile(companyName: string, jobId: string, fileType: string): Observable<HttpResponse<Blob>> {
    const url = `${this.baseUrl}/${companyName}/jobs/${jobId}/file/${fileType}`;
    return this.http.get<Blob>(url, {
      responseType: 'blob' as 'json',
      observe: 'response',
    });
  }

  getJobErrors(companyName: string, jobId: string, errorType: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/jobs/${jobId}/errors/${errorType}`);
  }
  getAllErrors(companyName: string, jobId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${companyName}/jobs/${jobId}/errors`);
  }

  getMeta(headers = null): Observable<any> {
    return this.http.get(`${this.baseUrl}/jobs/meta`, {
      params: {},
      headers,
    });
  }
}
